import { Component, Input, OnChanges } from '@angular/core';
import { LocalStore, MatchMediaService } from 'app/services';
import { Observable } from 'rxjs';
import { TelemetryItem } from '../../../services/state.service';

export const DetailsOrderedListLocalStore = new LocalStore('details-ordered-list');

@Component({
    selector: 'key-details-ordered-list',
    templateUrl: './details-ordered-list.component.html',
    styleUrls: ['./details-ordered-list.component.scss'],
    providers: [{ provide: LocalStore, useValue: DetailsOrderedListLocalStore }],
})
export class DetailsOrderedListComponent implements OnChanges {

    readonly PINNED_KEY = 'pinned';
    readonly ORDERED_KEY = 'ordered';
    readonly HIDDEN_KEY = 'hidden';

    @Input() title: string;
    @Input() telemetry: TelemetryItem[];

    isMobile$: Observable<boolean>;

    pinned: string[];
    ordered: string[];
    hidden: string[];

    constructor(
        private store: LocalStore,
        matchMedia: MatchMediaService) {
        this.pinned = this.store.get(this.PINNED_KEY) || [];
        this.ordered = this.store.get(this.ORDERED_KEY) || [];
        this.hidden = this.store.get(this.HIDDEN_KEY) || [];
        this.isMobile$ = matchMedia.isMobile;
    }

    ngOnChanges(): void {
        const displayedTelemetry = this.telemetry.map(d => d.name);
        // ensure that we only keep items visible in the ordered array
        this.ordered = this.ordered.filter(o => displayedTelemetry.includes(o));

        // make sure we add new items to our ordered list if they're not present elsewhere.
        const newEntries = displayedTelemetry.filter(item => 
            !this.pinned.includes(item) && 
            !this.hidden.includes(item) && 
            !this.ordered.includes(item)
        );
        newEntries.forEach(entry => this.ordered.push(entry));
    }

    onPinned(name: string) {
        // add to pinned array
        this.pinned = [...this.pinned, name];
        this.store.set(this.PINNED_KEY, this.pinned);

        // remove from ordered array
        this.ordered = this.ordered.filter(p => p !== name);
        this.store.set(this.ORDERED_KEY, this.ordered);
    }

    onUnPinned(name: string) {
        // remove from pinned array
        this.pinned = this.pinned.filter(p => p !== name);
        this.store.set(this.PINNED_KEY, this.pinned);

        // add to ordered array
        this.ordered = [...this.ordered, name];
        this.store.set(this.ORDERED_KEY, this.ordered);
    }

    onHidden(name: string) {
        // add to hidden array
        this.hidden = [...this.hidden, name];
        this.store.set(this.HIDDEN_KEY, this.hidden);

        // remove from ordered array
        this.ordered = this.ordered.filter(p => p !== name);
        this.store.set(this.ORDERED_KEY, this.ordered);
    }

    onShow(name: string) {
        // remove from hidden array
        this.hidden = this.hidden.filter(p => p !== name);
        this.store.set(this.HIDDEN_KEY, this.hidden);

        // add to ordered array
        this.ordered = [...this.ordered, name];
        this.store.set(this.ORDERED_KEY, this.ordered);
    }

    onUp(name: string, array: string[], storeId: string) {
        const position = array.findIndex(d => d === name);
        if (position >= 1) {
            this.swap(array, position, position - 1);
            this.store.set(storeId, array);

            // Update reference to trigger change detection for an array...
            this.telemetry = [...this.telemetry];
        }
    }

    onDown(name: string, array: string[], storeId: string) {
        const position = array.findIndex(d => d === name);
        if (position < array.length - 1) {
            this.swap(array, position, position + 1);
            this.store.set(storeId, array);

            // Update reference to trigger change detection for an array...
            this.telemetry = [...this.telemetry];
        }
    }

    reset() {
        this.pinned = [];
        this.hidden = [];
        this.ordered = this.telemetry?.map(t => t.name) || [];
        this.store.set(this.PINNED_KEY, []);
        this.store.set(this.HIDDEN_KEY, []);
        this.store.set(this.ORDERED_KEY, this.ordered);
    }

    private swap(array: string[], currentIndex: number, newIndex: number): void {
        // In place swap
        [array[currentIndex], array[newIndex]] = [array[newIndex], array[currentIndex]];
    }

}
